const TRACKING_PARAMETER_NAMES = new Set([
  '_ga',
  '_gl',
  'dclid',
  'fbclid',
  'gclid',
  'mc_cid',
  'mc_eid',
  'msclkid',
])

export interface NormalizedWebsiteUrl {
  /** URL limpia y segura para mostrar o abrir. */
  websiteUrl: string
  /** URL canónica que se persiste en `website_normalized`. */
  websiteNormalized: string
}

export interface ParsedEmails {
  /** Correos válidos, en minúsculas y sin duplicados. */
  emails: string[]
  /** Tokens inválidos tal como los escribió el usuario, salvo espacios externos. */
  invalidEmails: string[]
}

export interface EmailChipsValue {
  items: string[]
  draft: string
}

export class WebsiteUrlNormalizationError extends Error {
  constructor(message = 'Ingresa una URL web válida (http o https).') {
    super(message)
    this.name = 'WebsiteUrlNormalizationError'
  }
}

/** Elimina espacios externos y reduce cualquier espacio interno repetido. */
export function normalizeWhitespace(value: string): string {
  return value.trim().replace(/\s+/gu, ' ')
}

export const normalizeCompanyName = normalizeWhitespace
export const normalizeBusinessActivity = normalizeWhitespace

function hasScheme(value: string): boolean {
  return /^[a-z][a-z\d+.-]*:\/\//iu.test(value)
}

function removeTrackingParameters(url: URL): void {
  const keysToDelete = new Set<string>()

  url.searchParams.forEach((_value, key) => {
    const normalizedKey = key.toLowerCase()
    if (normalizedKey.startsWith('utm_') || TRACKING_PARAMETER_NAMES.has(normalizedKey)) {
      keysToDelete.add(key)
    }
  })

  keysToDelete.forEach((key) => url.searchParams.delete(key))
}

function removeTrailingSlashes(pathname: string): string {
  if (pathname === '/') return ''
  return pathname.replace(/\/+$/u, '')
}

function serializeWebsiteUrl(url: URL): string {
  const pathname = removeTrailingSlashes(url.pathname)
  return `${url.protocol}//${url.host}${pathname}${url.search}`
}

function prepareWebsiteUrl(value: string): URL {
  const trimmed = value.trim()
  if (!trimmed) throw new WebsiteUrlNormalizationError()

  const withProtocol = trimmed.startsWith('//')
    ? `https:${trimmed}`
    : hasScheme(trimmed)
      ? trimmed
      : `https://${trimmed}`

  let url: URL
  try {
    url = new URL(withProtocol)
  } catch {
    throw new WebsiteUrlNormalizationError()
  }

  if (!['http:', 'https:'].includes(url.protocol) || !url.hostname) {
    throw new WebsiteUrlNormalizationError()
  }

  if (url.username || url.password) {
    throw new WebsiteUrlNormalizationError('La URL no debe incluir credenciales.')
  }

  url.hostname = url.hostname.toLowerCase().replace(/\.$/u, '')
  url.hash = ''
  removeTrackingParameters(url)

  return url
}

/**
 * Normaliza una URL para presentación y comparación.
 *
 * La versión canónica considera equivalentes HTTP/HTTPS y `www`, ordena los
 * parámetros funcionales restantes y elimina las barras finales.
 */
export function normalizeWebsiteUrl(value: string): NormalizedWebsiteUrl {
  const displayUrl = prepareWebsiteUrl(value)
  const canonicalUrl = new URL(displayUrl.toString())

  canonicalUrl.protocol = 'https:'
  canonicalUrl.hostname = canonicalUrl.hostname.replace(/^www\./iu, '')
  canonicalUrl.searchParams.sort()

  return {
    websiteUrl: serializeWebsiteUrl(displayUrl),
    websiteNormalized: serializeWebsiteUrl(canonicalUrl),
  }
}

export function tryNormalizeWebsiteUrl(value: string): NormalizedWebsiteUrl | null {
  try {
    return normalizeWebsiteUrl(value)
  } catch {
    return null
  }
}

export function normalizeEmail(value: string): string {
  return value.trim().toLowerCase()
}

/** Separa correos por coma, punto y coma o cualquier espacio/salto de línea. */
export function splitEmailInput(input: string | readonly string[]): string[] {
  const values = typeof input === 'string' ? [input] : input
  return values.flatMap((value) => value.split(/[,;\s]+/u)).filter(Boolean)
}

export function isValidEmail(value: string): boolean {
  const email = normalizeEmail(value)
  if (email.length === 0 || email.length > 254) return false

  const separatorIndex = email.indexOf('@')
  if (separatorIndex <= 0 || separatorIndex !== email.lastIndexOf('@')) return false

  const localPart = email.slice(0, separatorIndex)
  const domain = email.slice(separatorIndex + 1)

  if (
    localPart.length > 64 ||
    localPart.startsWith('.') ||
    localPart.endsWith('.') ||
    localPart.includes('..') ||
    !/^[a-z\d.!#$%&'*+/=?^_`{|}~-]+$/iu.test(localPart)
  ) {
    return false
  }

  if (domain.length === 0 || domain.length > 253 || !domain.includes('.')) return false

  return domain
    .split('.')
    .every(
      (label) =>
        label.length > 0 && label.length <= 63 && /^[a-z\d](?:[a-z\d-]*[a-z\d])?$/iu.test(label),
    )
}

/** Normaliza y deduplica correos, conservando cada token inválido para reportarlo. */
export function parseEmailInput(input: string | readonly string[]): ParsedEmails {
  const emails: string[] = []
  const invalidEmails: string[] = []
  const seenEmails = new Set<string>()

  for (const token of splitEmailInput(input)) {
    const normalized = normalizeEmail(token)
    if (!isValidEmail(normalized)) {
      invalidEmails.push(token.trim())
      continue
    }

    if (!seenEmails.has(normalized)) {
      seenEmails.add(normalized)
      emails.push(normalized)
    }
  }

  return { emails, invalidEmails }
}

export const normalizeEmails = parseEmailInput

export function parseEmailChips(value: EmailChipsValue): ParsedEmails {
  return parseEmailInput([...value.items, value.draft])
}
